import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import type { PlannerMode, TruckPreference } from "./api";
import {
  INSPECT_MAX,
  MIN_VISIT_MAX,
  MODE_NAME,
  MODE_TEXT,
  minVisitText,
  type PlannerForm,
  plannerChange,
  plannerForm,
  PREFERENCE_TEXT,
  TEMPLATE_MAX,
  TEMPLATE_MIN,
  templateDaysText,
} from "./planner";
import { Skeleton } from "./Skeleton";

/** Rules V-10 to V-13: the mode, the truck preference, the template window, the minimum visit period and the inspect weight. */
export function PlannerSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.PlannerSettings | "failed" | undefined>(undefined);
  const [form, setForm] = useState<PlannerForm | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [problem, setProblem] = useState<{ key: keyof PlannerForm; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);
  const inputs = useRef<Partial<Record<keyof PlannerForm, HTMLInputElement | null>>>({});

  useEffect(() => {
    if (problem) inputs.current[problem.key]?.focus();
  }, [problem]);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.plannerSettings)
      .then(
        (next) => {
          setFound(next);
          setForm(plannerForm(next.planner));
        },
        () => setFound("failed"),
      );
  }, [user]);

  if (found === "failed") {
    return (
      <fieldset>
        <legend>Route planner</legend>
        <p className="error">Cannot load the route planner settings. Try again later.</p>
      </fieldset>
    );
  }
  if (found === undefined || form === null) {
    return (
      <fieldset aria-busy="true">
        <legend>Route planner</legend>
        <Skeleton shape="form" label="Loading the route planner settings" />
      </fieldset>
    );
  }
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const change = plannerChange(form);
    if ("key" in change) {
      setMessage(null);
      setProblem(change);
      return;
    }
    setProblem(null);
    setSaving(true);
    setMessage(null);
    try {
      const next = await api.setPlannerSettings(await user.getIdToken(), change);
      setFound(next);
      setForm(plannerForm(next.planner));
      setMessage({ ok: true, text: "Saved. The next plan uses the new settings." });
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The route planner settings were not saved. Try again." });
    } finally {
      setSaving(false);
      status.current?.focus();
    }
  };
  const edit = (next: Partial<PlannerForm>) => {
    setForm({ ...form, ...next });
    setMessage(null);
    setProblem(null);
  };
  const defaults = found.defaults;
  const error = (key: keyof PlannerForm) =>
    problem?.key === key ? (
      <p id={`planner-${key}-error`} role="alert" className="error" data-testid={`planner-${key}-error`}>
        {problem.text}
      </p>
    ) : null;
  const described = (key: keyof PlannerForm) => (problem?.key === key ? `planner-${key}-error planner-${key}-hint` : `planner-${key}-hint`);
  return (
    <form onSubmit={save} noValidate data-testid="planner-settings">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Route planner</legend>
        <div className="limit">
          <label htmlFor="planner-mode">Planner mode</label>
          <select id="planner-mode" data-testid="planner-mode" value={form.mode} aria-describedby="planner-mode-hint" onChange={(e) => edit({ mode: e.target.value as PlannerMode })}>
            {(Object.keys(MODE_TEXT) as PlannerMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {MODE_TEXT[mode]}
              </option>
            ))}
          </select>
          <p id="planner-mode-hint" className="hint">
            In full auto, the routes can mix the areas. In the area mode, each truck stays in its home areas (Manage &gt; Fleet). A truck with no home area goes to each
            area. When 2 or more trucks with home areas are not in service, the plan of that day uses full auto. Default: {MODE_NAME[defaults.mode]}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="planner-truck-preference">Truck preference in the area mode</label>
          <select
            id="planner-truck-preference"
            data-testid="planner-truck-preference"
            value={form.truckPreference}
            aria-describedby="planner-truck-preference-hint"
            onChange={(e) => edit({ truckPreference: e.target.value as TruckPreference })}
          >
            {(Object.keys(PREFERENCE_TEXT) as TruckPreference[]).map((preference) => (
              <option key={preference} value={preference}>
                {PREFERENCE_TEXT[preference]}
              </option>
            ))}
          </select>
          <p id="planner-truck-preference-hint" className="hint">
            Hard: a truck stays in its home areas, with these exceptions. A container with an overflow risk of 10% or more can go to each truck. Each truck can also go to a
            container with a due minimum visit, an account with no area, and an area with no home truck in the plan. Soft: a stop in the home area of a different truck costs
            one hour. Full auto does not use this setting. Default:{" "}
            {defaults.truck_preference}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="planner-template-days">Keep the stop order of an area (days)</label>
          <input
            id="planner-template-days"
            data-testid="planner-template-days"
            inputMode="numeric"
            ref={(element) => {
              inputs.current.templateDays = element;
            }}
            value={form.templateDays}
            aria-invalid={problem?.key === "templateDays" || undefined}
            aria-describedby={described("templateDays")}
            onChange={(e) => edit({ templateDays: e.target.value })}
          />
          {error("templateDays")}
          <p id="planner-templateDays-hint" className="hint">
            Only in the area mode. In this window, the route of an area visits its stops in the same order. A new account goes into the order and gets its first visit
            soon. The route lab gives 2 to 3 more overflows a week with a fixed order; if the drivers need one, use 90. 0 turns it off. 0, or from {TEMPLATE_MIN} to{" "}
            {TEMPLATE_MAX}. Default: {templateDaysText(defaults.template_days)}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="planner-min-visit">Minimum visit period (days)</label>
          <input
            id="planner-min-visit"
            data-testid="planner-min-visit"
            inputMode="numeric"
            ref={(element) => {
              inputs.current.minVisit = element;
            }}
            value={form.minVisit}
            aria-invalid={problem?.key === "minVisit" || undefined}
            aria-describedby={described("minVisit")}
            onChange={(e) => edit({ minVisit: e.target.value })}
          />
          {error("minVisit")}
          <p id="planner-minVisit-hint" className="hint">
            The planner visits each regular or new account in season at least once in this period, also when the container is probably not full. Call accounts are not
            included. 0 turns the rule off. From 0 to {MIN_VISIT_MAX}. Default: {minVisitText(defaults.min_visit_days)}.
          </p>
        </div>
        <div className="limit">
          <label htmlFor="planner-inspect">Weight of a visit on its last allowed day (%)</label>
          <input
            id="planner-inspect"
            data-testid="planner-inspect"
            inputMode="decimal"
            ref={(element) => {
              inputs.current.inspect = element;
            }}
            value={form.inspect}
            aria-invalid={problem?.key === "inspect" || undefined}
            aria-describedby={described("inspect")}
            onChange={(e) => edit({ inspect: e.target.value })}
          />
          {error("inspect")}
          <p id="planner-inspect-hint" className="hint">
            A share of the cost of an overflow. The weight halves for each workday before the last allowed day. From 0% to {INSPECT_MAX}%. Default:{" "}
            {Math.round(defaults.inspect_weight * 1000) / 10}%.
          </p>
        </div>
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-planner">
              {saving ? "Saving…" : "Save the planner settings"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="planner-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
